import { EApiTest } from '@datatype/iyagigo/api-type';
import React from 'react';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import TextBlock from '@component/common/block/text-block';
import { actions as modalActions } from '@store/slice/modal-slice';
import { useTypedDispatch } from '@hook/common/useStore';
import { ITodayTestModalData } from '@datatype/iyagigo/type';
import { createTestPath } from '@constant/iyagigo/route-path';
import { useNavigate } from 'react-router-dom';
import TextButton from '@design-system/text-button';
import { ETheme } from '@datatype/type';

interface IProps {
  data: ITodayTestModalData['data'];
}

// ############### type ###############
const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: ${size.MEDIUM_MODAL_WIDTH};
`;

const ContentContainer = styled.div`
  width: 100%;
`;

const TestMetadataContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background-color: ${color.BROWN_4};
  padding: ${size.MARGIN_1};
  margin: ${size.MARGIN_1} 0px ${size.MARGIN_3} 0px;
`;

const ProblemCountContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

const JLPT_METADATA = {
  // vocabulary: { name: '문자·어휘', count: 5 },
  // grammar: { name: '문법', count: 3 },
  // reading: { name: '독해', count: 2 },
  reading: { name: '언어지식', count: 6 },
  listening: { name: '청해', count: 4 },
} as const;

const TOEIC_METADATA = {
  rc: { name: 'RC', count: 6 },
  lc: { name: 'LC', count: 4 },
} as const;

const TodayTestModal: React.FC<IProps> = ({ data }) => {
  // ############### state ###############
  const navigate = useNavigate();

  // ############### state ###############
  const dispatch = useTypedDispatch();

  const TEST_METADATA = data.test.startsWith('TOEIC') ? TOEIC_METADATA : JLPT_METADATA;

  // ############### handler ###############
  const handleStudyButtonClick = () => {
    dispatch(modalActions.hideModal());
    // dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu: 'test', subMenu: data.test }));
    navigate(createTestPath('problem'));
  };

  // ############### variable ###############
  let title: string;

  switch (data.test) {
    case EApiTest.TOEIC950:
      title = 'TOEIC 900점';
      break;
    case EApiTest.TOEIC850:
      title = 'TOEIC 800점';
      break;
    case EApiTest.TOEIC750:
      title = 'TOEIC 700점';
      break;
    case EApiTest.TOEIC650:
      title = 'TOEIC 600점';
      break;
    case EApiTest.TOEIC550:
      title = 'TOEIC 500점';
      break;
    case EApiTest.JLPT1:
      title = 'JLPT N1';
      break;
    case EApiTest.JLPT2:
      title = 'JLPT N2';
      break;
    case EApiTest.JLPT3:
      title = 'JLPT N3';
      break;
    case EApiTest.JLPT4:
      title = 'JLPT N4';
      break;
    case EApiTest.JLPT5:
      title = 'JLPT N5';
      break;
    default:
      throw new Error('invalid today test');
  }

  // ############### component ###############
  const TestMetadata = Object.values(TEST_METADATA).map((category) => (
    <ProblemCountContainer key={category.name}>
      <TextBlock
        text={category.name}
        design={{
          text: { size: 'medium', color: color.BLACK_2, bold: false },
          box: { alignX: 'left' },
        }}
      />
      <TextBlock
        text={`${category.count}문제`}
        design={{
          text: { size: 'medium', color: color.BLACK_2, bold: false },
          box: { alignX: 'left' },
        }}
      />
    </ProblemCountContainer>
  ));

  return (
    <Wrapper>
      <ContentContainer>
        <TextBlock
          text={`오늘의 ${title} 문제`}
          design={{
            text: { size: 'medium', color: color.BLACK_2, bold: true },
            box: { alignX: 'left' },
          }}
        />
        <TestMetadataContainer>{TestMetadata}</TestMetadataContainer>
        <TextButton text='시작하기' theme={ETheme.BROWN} size='medium' shape='pill' width='100%' handleClick={handleStudyButtonClick} />
      </ContentContainer>
    </Wrapper>
  );
};

export default TodayTestModal;
